<script setup lang="ts">
import type { BridgingLoan, Period, PeriodLegacy } from '@shared/models';
import { computed, reactive } from 'vue';
import { firstLetterToUppercase, getSanitizedUrlParameters, removeCookieConsent } from './utils/DashboardHelper';
import { formatPercentage, getFixedPeriodInYears, isLegacyFormatApi } from '@/utils';
import { HeaderLabels, HeaderLabelsLegacy, MortgageProduct, SustainabilityDiscount } from '@/constants';
import { Locale, translate } from '@/i18n';
import { MortgageTypeIrt, PayoutType, RiskCategories, RiskCategoriesLegacy, RiskCategoryType, RiskCategoryTypeLegacy, SustainableRiskCategory } from '@shared/constants';
import DropDown from '@/components/DropDown/DropDown.vue';
import { EXTENDED_MORTGAGE_TYPES_KEYS } from '@/views/Dashboard/constants';
import { getInterestRates } from '@/services/InterestRatesService/InterestRatesService';
import InterestRatesTable from '@/components/InterestRatesTable/InterestRatesTable.vue';
import IText from '@/components/IText/IText.vue';
import type { RatesParams } from '@/models';
import ScAnalyticsApi from '@aab/sc-analytics-api';
import { useI18nStore } from '@/stores/i18n';
import { useRouter } from 'vue-router';

const { annuities, linear, interestFree, equity, sustainable, credit } = MortgageTypeIrt;

const DEFAULT_MORTGAGE_TYPES_KEYS = [annuities, linear, interestFree, equity, sustainable];
const SHOWN_INTEREST_PERIODS = [0, 5, 10, 15, 20, 25, 30];

const extendedMortgageTypes = computed((): any => {
  return [...EXTENDED_MORTGAGE_TYPES_KEYS].map((value) => ({ name: translate(`label:${value.toLowerCase()}`), value }));
});

const defaultMortgageTypes = computed((): any => {
  return [...DEFAULT_MORTGAGE_TYPES_KEYS, 'more-types'].map((value) => ({ name: translate(`label:${value.toLowerCase()}`), value }));
});

const mortgageValidityPeriods = computed((): any => {
  return [
    { name: translate('label:budgetLabel'), value: MortgageProduct.budget },
    { name: translate('label:woningLabel'), value: MortgageProduct.house }
  ];
});

const duurzaamheidskortingTypes = computed((): any => {
  return [
    { name: translate('label:noDuurzaamheidskorting'), value: SustainabilityDiscount.NoDiscount },
    { name: translate('label:duurzaamheidskortingA'), value: SustainabilityDiscount.A_OR_HIGHER },
    { name: translate('label:duurzaamheidskortingB'), value: SustainabilityDiscount.B }
  ];
});
const state = reactive({
  defaultMortgageTypes: defaultMortgageTypes,
  extendedMortgageTypes: extendedMortgageTypes,
  mortgageType: annuities,
  mortgageValidityPeriods: mortgageValidityPeriods,
  mortgageProduct: MortgageProduct.budget,
  duurzaamheidskortingTypes: duurzaamheidskortingTypes,
  duurzaamheidskortingType: SustainabilityDiscount.NoDiscount,
  headerLabels: [] as ReadonlyArray<string>,
  huisbankkorting: false,
  duurzaamheidskorting: undefined as any,
  defaultMortgageType: '' as MortgageTypeIrt | string,

  // interest table
  periods: [] as ReadonlyArray<Period | PeriodLegacy>,
  riskCategories: [...RiskCategories.DEFAULT] as ReadonlyArray<RiskCategoryType | RiskCategoryTypeLegacy | PayoutType | SustainableRiskCategory>,

  // generic
  isLoading: true,
  isError: false,

  overbruggingskrediet: '',
  // deeplink
  overbruggingskredieten: [] as ReadonlyArray<BridgingLoan>,
  isLegacyFormatApi: false
});

ScAnalyticsApi.pageTrack({
  eventName: 'interest rates table page visit',
  widgetModule: 'interest rates table vue.js',
  widgetName: `interest-rates-${translate('segment')}`,
  widgetState: 'view',
  widgetTitle: 'Interest Rates Table Widget'
});

removeCookieConsent();
initializeServiceInputs(getSanitizedUrlParameters(useRouter().currentRoute.value.query));
getInterestRateCategories(state.mortgageType);
getRates(state.mortgageProduct, state.mortgageType, state.huisbankkorting, state.duurzaamheidskorting);

function initializeServiceInputs(ratesParamsObj: RatesParams): void {
  if (ratesParamsObj) {
    // from deeplink
    state.mortgageProduct = ratesParamsObj.mortgageProduct;
    state.mortgageType = ratesParamsObj.mortgageType;
    state.defaultMortgageType = ratesParamsObj.mortgageType;
    state.huisbankkorting = ratesParamsObj.huisbankkorting;
    state.duurzaamheidskorting = ratesParamsObj.duurzaamheidskorting;
    state.duurzaamheidskortingType = state.duurzaamheidskorting ? SustainabilityDiscount.A_OR_HIGHER : SustainabilityDiscount.NoDiscount;

    if (state.mortgageType === sustainable) {
      state.huisbankkorting = true;
      state.duurzaamheidskorting = false;
      state.duurzaamheidskortingType = SustainabilityDiscount.NoDiscount;
    }
  } else {
    state.mortgageProduct = MortgageProduct.budget;
    state.mortgageType = annuities;
    state.defaultMortgageType = '';
    state.huisbankkorting = true;
    state.duurzaamheidskorting = false;
    state.duurzaamheidskortingType = SustainabilityDiscount.NoDiscount;
  }
}

function getDate(date: Date, locale: Locale): string | Date {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  return firstLetterToUppercase(date.toLocaleString(locale, options));
}

function sortOverbruggingskredieten(overbruggingskredieten: ReadonlyArray<BridgingLoan>): ReadonlyArray<BridgingLoan> {
  if (overbruggingskredieten === undefined || overbruggingskredieten.length === 0) return [];

  const overbruggingskredietenSortable = overbruggingskredieten.toSorted();
  // eslint-disable-next-line functional/immutable-data
  return overbruggingskredietenSortable.sort((a, b) => b.duration - a.duration);
}

function getRates(mortgageProduct: MortgageProduct, mortgageType: MortgageTypeIrt, huisbankkorting: boolean, duurzaamheidskorting: boolean): void {
  state.isLoading = true;
  getInterestRates(mortgageProduct, mortgageType, huisbankkorting, duurzaamheidskorting, state.duurzaamheidskortingType)
    .then(({ data }) => {
      state.isLegacyFormatApi = isLegacyFormatApi(data);
      getInterestRateCategories(state.mortgageType);
      state.periods = curateInterestTableInputs(state.mortgageType, data.periods);
      state.overbruggingskredieten = sortOverbruggingskredieten(data.overbruggingskredieten ?? []);
    })
    .catch(() => {
      state.isError = true;
    })
    .finally(() => {
      state.isLoading = false;
    });
}

function selectMortgageType(event: CustomEvent): void {
  const value: MortgageTypeIrt = event.detail.value;
  if (state.mortgageType === value) return;

  state.mortgageType = value;
  ScAnalyticsApi.eventTrack({
    eventName: 'click',
    eventCategory: 'mortgage type selection',
    eventAction: `irt: mortgage type selected: ${value}`
  });

  if (state.mortgageType !== sustainable) {
    getRates(state.mortgageProduct, state.mortgageType, state.huisbankkorting, state.duurzaamheidskorting);
  } else {
    getRates(state.mortgageProduct, state.mortgageType, state.huisbankkorting, false);
  }
}

function selectMortgageProduct(event: CustomEvent): void {
  const value: MortgageProduct = event.detail.value;

  if (state.mortgageProduct === value) return;
  state.mortgageProduct = value;
  ScAnalyticsApi.eventTrack({
    eventName: 'click',
    eventCategory: 'mortgage product selection',
    eventAction: `irt: mortgage product selected: ${value}`
  });
  getRates(state.mortgageProduct, state.mortgageType, state.huisbankkorting, state.duurzaamheidskorting);
}

function selectHuisbankorting(): void {
  state.huisbankkorting = !state.huisbankkorting;

  ScAnalyticsApi.eventTrack({
    eventName: 'click',
    eventCategory: 'mortgage discounts',
    eventAction: `irt: huisbankorting set to ${state.huisbankkorting}`
  });

  getRates(state.mortgageProduct, state.mortgageType, state.huisbankkorting, state.duurzaamheidskorting);
}

function selectDuurzaamheidskorting(event: CustomEvent): void {
  const value: SustainabilityDiscount = event.detail.value;
  state.duurzaamheidskorting = !(value === SustainabilityDiscount.NoDiscount);
  state.duurzaamheidskortingType = value;

  ScAnalyticsApi.eventTrack({
    eventName: 'click',
    eventCategory: 'mortgage discounts',
    eventAction: `irt: duurzaamheidskorting set to ${state.duurzaamheidskorting}`,
    eventLabel: state.duurzaamheidskortingType
  });
  getRates(state.mortgageProduct, state.mortgageType, state.huisbankkorting, state.duurzaamheidskorting);
}

function curateInterestTableInputs(mortgageType: MortgageTypeIrt, periods: ReadonlyArray<Period | PeriodLegacy>): ReadonlyArray<Period | PeriodLegacy> {
  if (mortgageType === equity || mortgageType === sustainable) {
    periods.forEach((interestPeriod) => {
      // TODO (S.Panfilov) any (rate is PeriodRate, but here we have a param reassign)
      interestPeriod.rates.forEach((rate: any) => {
        // TODO (S.Panfilov) eslint disabled
        // eslint-disable-next-line functional/immutable-data, no-param-reassign
        rate.ltv = mortgageType === equity ? rate.payout : SustainableRiskCategory.Rent;
      });
    });
  }

  return periods;
}

function getInterestRateCategories(mortgageType: MortgageTypeIrt): void {
  const labels: Record<string, ReadonlyArray<string>> = state.isLegacyFormatApi ? HeaderLabelsLegacy : HeaderLabels;
  const categories: Record<string, ReadonlyArray<RiskCategoryType | RiskCategoryTypeLegacy | PayoutType | SustainableRiskCategory>> = state.isLegacyFormatApi ? RiskCategoriesLegacy : RiskCategories;
  state.headerLabels = (labels[mortgageType] ? labels[mortgageType] : labels.DEFAULT).map((label: string) => translate(label));
  state.riskCategories = categories[mortgageType] ? categories[mortgageType] : categories.DEFAULT;
}

function displayOverbruggingskrediet(): boolean {
  return [equity, credit].indexOf(state.mortgageType) < 0;
}

function getHuisbankkortingLabel(isCheckbox: boolean): string {
  return isCheckbox ? translate('label:huisbankkorting') : translate('text:huisbankkorting');
}

function getOverbruggingskredietPeriod(krediet: BridgingLoan): string {
  return krediet.duration === 1 ? `(${translate('text:variable')})` : `(${getFixedPeriodInYears(krediet)} ${translate('text:year')})`;
}
</script>

<!-- eslint-disable vue/no-deprecated-slot-attribute -->
<template>
  <div id="innerPage" class="dashboard">
    <div id="viewPortSize" class="d-none d-sm-inline" />
    <div v-if="!state.isError">
      <div class="row mr-0">
        <div class="col-xl-3 col-sm-6 pb-2">
          <div class="d-flex">
            <label class="text-heavy-green dashboard__label -select-label" for="mortgage_type_selector">
              {{ $t('label:mortgageTypeSelectorTop') }}
            </label>
          </div>
          <DropDown
            :id="'mortgage_type_selector'"
            :label="$t('label:mortgageTypeSelectorTop')"
            :default-option="state.mortgageType"
            :options="state.defaultMortgageTypes"
            :extended-options="state.extendedMortgageTypes"
            @selection-changed="selectMortgageType"
          />
        </div>
        <div class="col-xl-3 col-sm-6 pb-2">
          <div class="d-flex">
            <label class="text-heavy-green dashboard__label -select-label" for="mortgage_product_selector">{{ $t('label:mortgageProduct') }}</label>
            <IText class="itext-dropdown ml-1" :name="'mortgage type'">
              <div>{{ $t('iText:mortgageProduct') }}</div>
              <ul class="itext-dropdown__list">
                <li>{{ $t('iText:differenceOne') }}</li>
                <li>{{ $t('iText:differenceTwo') }}</li>
                <li>{{ $t('iText:differenceThree') }}</li>
                <li>{{ $t('iText:differenceFour') }}</li>
                <li>
                  {{ $t('iText:differenceFive') }}
                  <aab-link target="_blank" :url="$t('linkUrl:mortgageProduct')" rel="noopener noreferrer">
                    {{ $t('linkText:here') }}
                  </aab-link>
                  .
                </li>
              </ul>
            </IText>
          </div>
          <aab-select
            id="mortgage_product_selector"
            class="dashboard__select"
            :aab-select-items="JSON.stringify(state.mortgageValidityPeriods)"
            :aab-select-value="state.mortgageProduct"
            @aab-select-value-change="selectMortgageProduct"
          />
        </div>
        <div v-show="state.mortgageType !== MortgageTypeIrt.credit" class="col-xl-3 col-sm-6 pb-2">
          <div class="d-flex">
            <span class="text-heavy-green dashboard__label -select-label">{{ $t('label:discount') }}</span>
            <IText class="itext-dropdown ml-1" :name="'huisbankkorting'">
              {{ $t('iText:huisbankkorting') }}
            </IText>
          </div>
          <div class="flex-tab-extra-checkbox-first">
            <aab-checkbox>
              <input id="huisbankkorting_check" slot="input-slot" type="checkbox" class="dashboard__checkbox" :checked="state.huisbankkorting" @change="selectHuisbankorting" />
              <label slot="label-slot" for="huisbankkorting_check"> {{ getHuisbankkortingLabel(true) }}</label>
            </aab-checkbox>
          </div>
        </div>
        <div v-show="state.mortgageType !== MortgageTypeIrt.sustainable && state.mortgageType !== MortgageTypeIrt.credit" class="col-xl-3 col-sm-6 pb-2">
          <div class="d-flex">
            <label class="text-heavy-green dashboard__label -select-label" for="duurzaamheidskorting_selector">{{ $t('label:duurzaamheidskorting') }}</label>
            <IText class="itext-dropdown ml-1" :name="'duurzaamheidskorting'">
              <div>{{ $t('iText:duurzaamheidskorting') }}</div>
              <aab-link target="_blank" :url="$t('linkUrl:duurzaamheidskorting')" rel="noopener noreferrer" type="single">
                {{ $t('linkText:duurzaamheidskorting') }}
              </aab-link>
            </IText>
          </div>
          <aab-select
            id="duurzaamheidskorting_selector"
            class="dashboard__select"
            :aab-select-items="JSON.stringify(state.duurzaamheidskortingTypes)"
            :aab-select-value="state.duurzaamheidskortingType"
            @aab-select-value-change="selectDuurzaamheidskorting"
          />
        </div>
      </div>
      <div id="col8Div" class="px-0 mt-1">
        <div class="spl-sm-1">
          <label class="text-heavy-green dashboard__label -label-date">{{ getDate(new Date(), $t('portal')) }}</label>
          <small id="explanationText" class="dashboard__subtitle text-justify">
            {{ $t('text:rates') + $t('text:' + state.mortgageType) + $t('text:' + state.mortgageProduct) + $t('text:mortgage') }}
            <span v-if="state.mortgageType !== MortgageTypeIrt.credit">
              {{ (state.huisbankkorting ? $t('text:with') : $t('text:without')) + getHuisbankkortingLabel(false) }}
            </span>
            <span v-if="state.mortgageType !== MortgageTypeIrt.sustainable && state.mortgageType !== MortgageTypeIrt.credit">,
              {{ (state.duurzaamheidskorting ? $t('text:with') : $t('text:without')) + $t('text:duurzaamheidskorting') }}
            </span>
          </small>
        </div>
        <InterestRatesTable
          v-if="!state.isLoading && !state.isError"
          :interest-periods="state.periods"
          :shown-interest-periods="SHOWN_INTEREST_PERIODS"
          :header-labels="state.headerLabels"
          :used-rate-categories="state.riskCategories"
        />
        <div v-if="!state.isLoading" class="bring-forward spl-sm-1 d-flex">
          <div v-if="displayOverbruggingskrediet()" class="dashboard__bridgingloans">
            <div v-for="krediet in state.overbruggingskredieten" :key="krediet.duration" class="dashboard__bridgingloan">
              <label :id="`bridgingLoan${krediet.duration}`" class="text-heavy-green dashboard__label -credit">
                {{ $t('label:overbruggingskrediet') }}
                <span> {{ getOverbruggingskredietPeriod(krediet) }} </span>
              </label>
              <span>{{ formatPercentage(krediet.rate, useI18nStore().locale) }}</span>
            </div>
          </div>
        </div>
        <div v-if="!state.isLoading" class="bring-forward spl-sm-1">
          <hr class="mt-0 mb-1" />
          <small class="text-muted text-justify">{{ $t('text:disclaimer') }}</small>
        </div>
        <div v-show="state.isLoading" class="dashboard__spinner-container">
          <aab-spinner class="dashboard__spinner" size="10" />
        </div>
      </div>
    </div>
    <div v-if="state.isError" class="em-shadow-tile bg-white px-1 py-2 row m-0">
      <div class="d-flex flex-column justify-content-center w-100">
        <h2 class="text-center">
          {{ $t('title:error') }}
        </h2>
        <h3 class="text-center">
          {{ $t('text:error') }}
        </h3>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@aab/sc-styles-colors/dist/scss/styles' as colors;
@use '@aab/sc-styles-spacing/dist/scss/styles' as spacing;
@use '@aab/sc-styles-utilities/dist/scss/styles' as utilities;
@use '~@aab/sc-styles-grid/dist/scss/styles.scss' as styles;
@use '@/assets/_utils.scss' as utils;

@include styles.row;
@include styles.col-sm-6;
@include styles.col-xl-3;

@include spacing.py-2;
@include spacing.pb-2;
@include spacing.px-1;
@include spacing.mt-1;
@include spacing.ml-1;
@include spacing.pt-1;
@include spacing.spl-sm-1;
@include spacing.mb-1;
@include spacing.m-0;
@include spacing.mb-0;
@include spacing.mr-0;
@include spacing.px-0;
@include spacing.mt-0;

@include utilities.d-none;
@include utilities.d-sm-inline;
@include utilities.d-flex;
@include utilities.justify-content-between;
@include utilities.flex-column;
@include utilities.justify-content-center;

.dashboard {
  background-color: colors.$white;
  min-height: utils.px-to-rem(480px);

  &__label {
    display: block;
    margin-bottom: 0.5rem;

    &.-label-date,
    &.-select-label,
    &.-credit {
      font-weight: 500;
    }
  }

  &__checkbox {
    margin-right: 12px;
  }

  &__subtitle {
    display: block;
  }

  &__spinner-container {
    background-color: colors.$gm55;
    width: 100%;
    height: 310px;
    position: relative;
  }
  /*In order to create space between bridgingloan rates*/

  &__bridgingloans {
    display: flex;
    width: 100%;
    margin-bottom: 1rem;
    flex-direction: column;
    margin-top: 1rem;

    @media (min-width: 475px) {
      flex-direction: row;
    }
  }
  &__bridgingloan {
    @media (min-width: 475px) {
      flex: 0 0 50%;
    }
  }

  .text-heavy-green {
    color: colors.$g400 !important;
  }
  .text-muted {
    color: #6c757d !important;
  }

  .fixed-width-280 {
    max-width: 280px;
    margin-bottom: 1rem;
  }
}

.itext-dropdown {
  &__list {
    padding-left: 20px;
    margin-bottom: 0;
  }
}
</style>
